import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { fullSelectionActions } from 'pages/documents/panels/bulk-document'
import Modal, { useModal } from 'modals/dialog'
import { ModalHeaderMessage } from './s'
import Name from './name'
import Concerns from './concerns'
import Category from './category'
import Access from './access'
import Download from './download'
import Remove from './remove'

const BulkEditDocuments = ({ which, progress, setProgress, onProgress, onDone, salt }) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()

    const {
        selection: fullSelection,
        getEditableSelection,

        updateNamesForSelection,
        updateConcernsForSelection,
        updateCategoryForSelection,
        updateAccessForSelection,
        downloadSelection,
        deleteSelection
    } = useDocuments()

    const selection = getEditableSelection()

    const [currentSelection, setCurrentSelection] = useState([...selection])
    const [failed, setFailed] = useState([])

    useEffect(() => {
        return () => {
            setCurrentSelection([])
            setFailed([])
        }
    }, [])

    const action = {
        name: updateNamesForSelection,
        concerns: updateConcernsForSelection,
        category: updateCategoryForSelection,
        access: updateAccessForSelection,
        download: downloadSelection,
        remove: deleteSelection
    }[which]

    const act = async (body, { resetTouched }) => {
        setProgress(progress => ({
            ...progress,
            busy: true
        }))

        const results = await action(body, { onProgress })

        setProgress(progress => ({
            ...progress,
            busy: false,
            settled: true
        }))

        if(results.every(({ status }) => status === 'fulfilled')) {
            resetTouched()
            setCurrentSelection([...selection])
            return void onDone()
        }

        setFailed(results.reduce((accumulator, { status }, index) => {
            if(status === 'fulfilled') {
                return accumulator
            }

            return [
                ...accumulator,
                currentSelection[index]
            ]
        }, []))
    }

    const actOnFullSelection = fullSelectionActions.includes(which)
    const count = actOnFullSelection ?
        fullSelection.length :
        selection.length

    const caption = !!captionTranslations[which] ?
        formatMessage(captionTranslations[which], { count }) :
        null

    const Interaction = {
        name: Name,
        concerns: Concerns,
        category: Category,
        access: Access,
        download: Download,
        remove: Remove
    }[which]

    return (
        <>
            <Modal.Header
                heading={formatMessage({
                    id: 'action_edit_bulk',
                    defaultMessage: 'Bulk edit'
                })}
                caption={caption}
                childrenClassName="fullwidth">
                {(!actOnFullSelection && selection.length < fullSelection.length) && (
                    <ModalHeaderMessage
                        type="warning"
                        className="spacious"
                        message={formatMessage({
                            id: 'documents_bulk_edit_warning_owner',
                            defaultMessage: '{count, plural, =0 {} =1 {1 of the documents you’ve selected is owned by someone else, and you can’t edit it. We will skip it.} other {{count} of the documents you’ve selected are owned by others. We will skip those.}}'
                        }, { count: fullSelection.length - selection.length })} />
                )}
            </Modal.Header>
            <Interaction
                act={act}
                failed={failed}
                progress={progress}
                dismiss={dismiss}
                salt={salt} />
        </>
    )
}

const captionTranslations = {
    name: {
        id: 'documents_bulk_caption_name',
        defaultMessage: '{count, plural, =0 {} =1 {Rename 1 document} other {Rename {count} documents}}'
    },
    concerns: {
        id: 'documents_bulk_caption_concerns',
        defaultMessage: '{count, plural, =0 {} =1 {Change who 1 document is regarding} other {Change who {count} documents are regarding}}'
    },
    category: {
        id: 'documents_bulk_caption_category',
        defaultMessage: '{count, plural, =0 {} =1 {Change the category of 1 document} other {Change the category of {count} documents}}'
    },
    access: {
        id: 'documents_bulk_caption_access',
        defaultMessage: '{count, plural, =0 {} =1 {Change access for 1 document} other {Change access for {count} documents}}'
    },
    download: {
        id: 'documents_bulk_caption_download',
        defaultMessage: '{count, plural, =0 {} =1 {Download 1 document} other {Download {count} documents}}'
    },
    remove: {
        id: 'documents_bulk_caption_remove',
        defaultMessage: '{count, plural, =0 {} =1 {Delete 1 document} other {Delete {count} documents}}'
    }
}

export default BulkEditDocuments